<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
// NOTICE: KAPTION-INTEGRATION
import { downloadFile } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';

import { useMessageContext } from '../provider.js';
import BaseAttachmentBubble from './BaseAttachment.vue';
import FileIcon from 'next/icon/FileIcon.vue';
// NOTICE: KAPTION-INTEGRATION - pdf-preview.BUBBLE.1
import KaptionPdfPreview from '../kaption/KaptionPdfPreview.vue';

const { attachments } = useMessageContext();

const { t } = useI18n();

const attachment = computed(() => {
  return attachments.value[0];
});

const url = computed(() => {
  return attachment.value.dataUrl;
});

const fileName = computed(() => {
  if (url.value) {
    const filename = url.value.substring(url.value.lastIndexOf('/') + 1);
    return filename || t('CONVERSATION.UNKNOWN_FILE_TYPE');
  }
  return t('CONVERSATION.UNKNOWN_FILE_TYPE');
});

const fileType = computed(() => {
  return fileName.value.split('.').pop();
});

// NOTICE: KAPTION-INTEGRATION
// pdf-preview.BUBBLE.1-3 — a PDF opens in the viewer instead of landing in the
// downloads folder. Everything else still downloads, which is all a browser can
// do with a .docx or a .zip anyway.
const showPreview = ref(false);

const isPdf = computed(() => {
  const extension = attachment.value.extension || fileType.value || '';
  return extension.toLowerCase() === 'pdf';
});

const openPreview = () => {
  showPreview.value = true;
};

// NOTICE: KAPTION-INTEGRATION
// A plain <a target="_blank"> opened documents inline instead of saving them —
// the Active Storage proxy serves PDFs with Content-Disposition: inline, so
// "Download" just rendered the PDF in a new tab (and a later browser "save"
// could lose the .pdf name, notably on Windows). Fetch the blob and force a
// named download instead — same pattern as the audio chips and GalleryView.
const downloadAttachment = async () => {
  try {
    await downloadFile({
      url: url.value,
      type: attachment.value.fileType || 'file',
      extension: attachment.value.extension || fileType.value,
    });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  }
};
</script>

<template>
  <BaseAttachmentBubble
    icon="i-teenyicons-user-circle-solid"
    icon-bg-color="bg-n-alpha-3 dark:bg-n-alpha-white"
    sender-translation-key="CONVERSATION.SHARED_ATTACHMENT.FILE"
    :content="decodeURI(fileName)"
    :action="
      isPdf
        ? { onClick: openPreview, label: $t('KAPTION_FILE_PREVIEW.VIEW') }
        : { onClick: downloadAttachment, label: $t('CONVERSATION.DOWNLOAD') }
    "
    @click="isPdf && openPreview()"
  >
    <template #icon>
      <FileIcon :file-type="fileType" class="size-4" />
    </template>
  </BaseAttachmentBubble>
  <!-- NOTICE: KAPTION-INTEGRATION - pdf-preview.BUBBLE.2 -->
  <KaptionPdfPreview
    v-if="showPreview"
    :url="url"
    :name="decodeURI(fileName)"
    :extension="attachment.extension || fileType"
    @close="showPreview = false"
  />
</template>
